import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { Track } from 'src/@types/tracks';
import axiosInstance from 'src/utils/axios';
import { APIS } from 'src/utils/serviceUrls';

// client-side-pagination-list-pages: server-side pagination + search for the
// admin Tracks grid (GET /tracks/list -> {total,page,pageSize,items}).
const PAGE_SIZE = 12;

export const useTracks = () => {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [filterName, setFilterName] = useState('');
  const [filterRole] = useState('all');
  const [currentTrack, setCurrentTrack] = useState<Track | undefined>();
  const [page, setPage] = useState(1); // 1-based (MUI Pagination)
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const getTrackList = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(APIS.TRACKS.PAGINATED, {
        params: { page, pageSize: PAGE_SIZE, search: filterName || undefined },
      });
      const data = response.data?.data ?? response.data;
      setTracks(Array.isArray(data?.items) ? data.items : []);
      setTotal(typeof data?.total === 'number' ? data.total : 0);
    } catch (error: any) {
      enqueueSnackbar(error?.message || 'Failed to load tracks', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleFilterName = (value: string) => {
    setFilterName(value);
    setPage(1);
  };

  const handleEditRow = (selected: Track) => {
    setCurrentTrack(selected);
    setModalOpen(true);
  };
  const handleDeleteRow = async (id: string) => {
    try {
      await axiosInstance.delete(APIS.TRACKS.PATCH(id));
      await axiosInstance.post(APIS.SUPPORT.handleDelete, {
        track: id,
        type: 'TRACK',
      });
    } catch (error: any) {
      console.error(error);
      enqueueSnackbar(error?.message || 'Unable to Delete!', { variant: 'error' });
    }
    setConfirmationOpen(true);
    getTrackList();
  };

  return {
    modalOpen,
    setModalOpen,
    confirmationOpen,
    setConfirmationOpen,
    filterName,
    filterRole,
    currentTrack,
    setCurrentTrack,
    filteredData: tracks,
    page,
    setPage,
    total,
    pageSize: PAGE_SIZE,
    loading,
    handleDeleteRow,
    handleEditRow,
    handleFilterName,
    getTrackList,
  };
};
